
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserHoverCard } from '@/components/ui/user-hover-card';
import { Textarea } from '@/components/ui/textarea';
import {
  MessageCircle,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Flag,
  Pin,
  Star,
  Trash2,
  Settings,
  Clock,
  User,
  Shield,
  Zap,
  SlidersHorizontal
} from 'lucide-react';
import { useCommentsStore } from '@/lib/stores/comments';
import { useAuthStore } from '@/lib/stores/auth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CommentModerationProps {
  streamId?: string;
  className?: string;
}

export function CommentModeration({ streamId, className }: CommentModerationProps) {
  const { user } = useAuthStore();
  const {
    comments,
    isLoading,
    error,
    filters,
    setFilters,
    moderateComment,
    bulkModerateComments,
    bulkDeleteComments,
    pinComment,
    highlightComment,
    flagComment,
    bulkFlagComments,
    clearError,
  } = useCommentsStore();

  const [selectedComments, setSelectedComments] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | 'flag' | 'delete' | null>(null);
  const [showAutoModDialog, setShowAutoModDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Filter comments based on current filters and search
  const filteredComments = useMemo(() => {
    let filtered = comments;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(comment =>
        comment.content.toLowerCase().includes(query) ||
        (comment.user?.name.toLowerCase().includes(query) ?? false)
      );
    }

    // Apply stream filter
    if (streamId) {
      filtered = filtered.filter(comment => comment.streamId === parseInt(streamId));
    }

    // Apply moderation filters
    if (filters.moderationStatus) {
      filtered = filtered.filter(comment => {
        switch (filters.moderationStatus) {
          case 'pending':
            return !comment.moderated;
          case 'approved':
            return comment.moderated && comment.moderationAction === 'approved';
          case 'rejected':
            return comment.moderated && comment.moderationAction === 'rejected';
          case 'flagged':
            return comment.moderated && comment.moderationAction === 'flagged';
          default:
            return true;
        }
      });
    }

    // Apply flagged filter
    if (filters.flaggedOnly) {
      filtered = filtered.filter(comment => comment.flaggedWords && comment.flaggedWords.length > 0);
    }

    // Apply spam filter
    if (filters.spamOnly) {
      filtered = filtered.filter(comment => (comment.spamScore || 0) > 0.7);
    }

    return filtered;
  }, [comments, searchQuery, streamId, filters]);

  const handleBulkAction = async () => {
    if (!bulkAction || selectedComments.size === 0) return;

    const commentIds = Array.from(selectedComments);

    try {
      if (bulkAction === 'approve' || bulkAction === 'reject') {
        await bulkModerateComments(commentIds, bulkAction === 'approve');
      } else if (bulkAction === 'flag') {
        await bulkFlagComments(commentIds, 'Bulk flagged by moderator');
      } else if (bulkAction === 'delete') {
        // Delete is handled via confirmation dialog
        return;
      }

      setSelectedComments(new Set());
      setBulkAction(null);
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const confirmDelete = async () => {
    const commentIds = Array.from(selectedComments);
    try {
      await bulkDeleteComments(commentIds);
      setSelectedComments(new Set());
      setBulkAction(null);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  };

  const handleSelectComment = (commentId: number, checked: boolean) => {
    setSelectedComments(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(commentId);
      } else {
        newSet.delete(commentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedComments(new Set(filteredComments.map(c => c.id)));
    } else {
      setSelectedComments(new Set());
    }
  };

  const getModerationStatusColor = (comment: { moderated: boolean; moderationAction?: string }) => {
    if (!comment.moderated) return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
    if (comment.moderationAction === 'approved') return 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400';
    if (comment.moderationAction === 'rejected') return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400';
    if (comment.moderationAction === 'flagged') return 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
    return 'bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
  };

  const getModerationStatusIcon = (comment: { moderated: boolean; moderationAction?: string }) => {
    if (!comment.moderated) return <Clock className="h-4 w-4" />;
    if (comment.moderationAction === 'approved') return <CheckCircle className="h-4 w-4" />;
    if (comment.moderationAction === 'rejected') return <XCircle className="h-4 w-4" />;
    if (comment.moderationAction === 'flagged') return <Flag className="h-4 w-4" />;
    return <Shield className="h-4 w-4" />;
  };

  // Moderation stats
  const stats = useMemo(() => {
    const allComments = streamId ? comments.filter(c => c.streamId === parseInt(streamId)) : comments;

    return {
      total: allComments.length,
      pending: allComments.filter(c => !c.moderated).length,
      approved: allComments.filter(c => c.moderationAction === 'approved').length,
      rejected: allComments.filter(c => c.moderationAction === 'rejected').length,
      flagged: allComments.filter(c => c.moderationAction === 'flagged').length,
      spam: allComments.filter(c => (c.spamScore || 0) > 0.7).length,
    };
  }, [comments, streamId]);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Comment Moderation
            </CardTitle>
            <CardDescription>
              Manage and moderate community comments
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            <Dialog open={showAutoModDialog} onOpenChange={setShowAutoModDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Auto Mod
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Auto Moderation Settings</DialogTitle>
                  <DialogDescription>
                    Configure automatic comment moderation rules
                  </DialogDescription>
                </DialogHeader>
                <AutoModerationSettings onSave={() => setShowAutoModDialog(false)} />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mt-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-xs text-muted-foreground">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <div className="text-xs text-muted-foreground">Approved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
            <div className="text-xs text-muted-foreground">Rejected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.flagged}</div>
            <div className="text-xs text-muted-foreground">Flagged</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.spam}</div>
            <div className="text-xs text-muted-foreground">Spam</div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              className="ml-auto"
            >
              Dismiss
            </Button>
          </Alert>
        )}

        {/* Search and Filters */}
        <div className="flex items-center justify-between gap-3">
          <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[min(92vw,420px)] p-0">
              <SheetHeader className="border-b p-4 pr-12">
                <SheetTitle>Moderation Filters</SheetTitle>
                <SheetDescription>Filter comments by text, moderation status, and moderation flags.</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 p-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Search</p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search comments..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Status</p>
                  <Select
                    value={filters.moderationStatus || 'all'}
                    onValueChange={(value) => setFilters({
                      ...filters,
                      moderationStatus: value === 'all' ? undefined : value as 'pending' | 'approved' | 'rejected' | 'flagged'
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="flagged">Flagged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      id="flagged-only"
                      checked={filters.flaggedOnly || false}
                      onCheckedChange={(checked) => setFilters({
                        ...filters,
                        flaggedOnly: checked as boolean
                      })}
                    />
                    Flagged only
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      id="spam-only"
                      checked={filters.spamOnly || false}
                      onCheckedChange={(checked) => setFilters({
                        ...filters,
                        spamOnly: checked as boolean
                      })}
                    />
                    Spam only
                  </label>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          {(searchQuery.trim() || filters.moderationStatus || filters.flaggedOnly || filters.spamOnly) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setFilters({
                  ...filters,
                  moderationStatus: undefined,
                  flaggedOnly: false,
                  spamOnly: false,
                });
              }}
            >
              Clear
            </Button>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedComments.size > 0 && (
          <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedComments.size === filteredComments.length}
                onCheckedChange={handleSelectAll}
                aria-label="Select all comments"
              />
              <span className="text-sm font-semibold">
                {selectedComments.size} comment{selectedComments.size !== 1 ? 's' : ''} selected
              </span>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <Select value={bulkAction || ''} onValueChange={(value) => setBulkAction(value as 'approve' | 'reject' | 'flag' | 'delete' | null)}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Choose action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approve">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Approve
                    </span>
                  </SelectItem>
                  <SelectItem value="reject">
                    <span className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      Reject
                    </span>
                  </SelectItem>
                  <SelectItem value="flag">
                    <span className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-orange-600" />
                      Flag
                    </span>
                  </SelectItem>
                  <SelectItem value="delete">
                    <span className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-red-600" />
                      Delete
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {bulkAction === 'delete' ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete {selectedComments.size}
                </Button>
              ) : (
                <Button
                  onClick={handleBulkAction}
                  size="sm"
                  disabled={!bulkAction}
                >
                  Apply Action
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedComments(new Set());
                  setBulkAction(null);
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Comments List */}
        <ScrollArea className="h-96">
          <div className="space-y-3">
            {filteredComments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No comments found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Select All */}
                <div className="flex items-center space-x-2 p-3 border rounded">
                  <Checkbox
                    checked={selectedComments.size === filteredComments.length && filteredComments.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                  <span className="text-sm font-medium">Select All</span>
                </div>

                {/* Comments */}
                {filteredComments.map((comment) => (
                  <div key={comment.id} className="p-4 border rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedComments.has(comment.id)}
                        onCheckedChange={(checked) => handleSelectComment(comment.id, checked as boolean)}
                      />

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {comment.user ? (
                            <UserHoverCard user={{ name: comment.user.name, profilePictureUrl: comment.user.profilePictureUrl || undefined }}>
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={comment.user.profilePictureUrl || undefined} />
                                <AvatarFallback className="text-xs">
                                  {comment.user.name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </UserHoverCard>
                          ) : (
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">?</AvatarFallback>
                            </Avatar>
                          )}
                          <span className="font-medium text-sm">{comment.user?.name || 'Unknown User'}</span>
                          <Badge
                            variant="outline"
                            className={`text-xs ${getModerationStatusColor(comment)}`}
                          >
                            {getModerationStatusIcon(comment)}
                            <span className="ml-1 capitalize">
                              {comment.moderated ? (comment.moderationAction || 'moderated') : 'pending'}
                            </span>
                          </Badge>

                          {comment.pinned && <Pin className="h-4 w-4 text-blue-500" />}
                          {comment.highlighted && <Star className="h-4 w-4 text-yellow-500" />}
                        </div>

                        <p className="text-sm mb-3">{comment.content}</p>

                        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                          <span>{new Date(comment.createdAt).toLocaleString()}</span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Likes: {comment.likes}
                          </span>
                          {comment.spamScore && comment.spamScore > 0 && (
                            <span className="flex items-center gap-1">
                              <Zap className="h-3 w-3" />
                              Spam: {(comment.spamScore * 100).toFixed(0)}%
                            </span>
                          )}
                          {comment.flaggedWords && comment.flaggedWords.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Flag className="h-3 w-3" />
                              Flagged: {comment.flaggedWords.join(', ')}
                            </span>
                          )}
                        </div>

                        {/* Moderation Actions */}
                        <div className="flex items-center gap-2">
                          {!comment.moderated && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => moderateComment(comment.id, true)}
                                disabled={isLoading}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => moderateComment(comment.id, false)}
                                disabled={isLoading}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => flagComment(comment.id, 'Manually flagged')}
                            disabled={isLoading}
                          >
                            <Flag className="h-4 w-4 mr-1" />
                            Flag
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => pinComment(comment.id, !comment.pinned)}
                            disabled={isLoading}
                          >
                            {comment.pinned ? 'Unpin' : 'Pin'}
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => highlightComment(comment.id, !comment.highlighted)}
                            disabled={isLoading}
                          >
                            {comment.highlighted ? 'Unhighlight' : 'Highlight'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Delete {selectedComments.size} Comment{selectedComments.size !== 1 ? 's' : ''}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. All selected comments will be permanently removed from the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Comments
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

// Auto Moderation Settings Component
interface AutoModerationSettingsProps {
  onSave: () => void;
}

type AutoModerationSettingsState = {
  enabled: boolean;
  bannedWords: string[];
  maxLength: number;
  requireApproval: boolean;
  spamThreshold: number;
  sentimentFilter: {
    enabled: boolean;
    minScore: number;
  };
  duplicateDetection: {
    enabled: boolean;
    similarityThreshold: number;
  };
  rateLimit: {
    maxCommentsPerMinute: number;
    maxCommentsPerHour: number;
  };
};

const defaultAutoModerationSettings: AutoModerationSettingsState = {
  enabled: true,
  bannedWords: ['spam', 'inappropriate'],
  maxLength: 500,
  requireApproval: false,
  spamThreshold: 0.7,
  sentimentFilter: {
    enabled: false,
    minScore: 0.3,
  },
  duplicateDetection: {
    enabled: true,
    similarityThreshold: 0.8,
  },
  rateLimit: {
    maxCommentsPerMinute: 5,
    maxCommentsPerHour: 20,
  },
};

function AutoModerationSettings({ onSave }: AutoModerationSettingsProps) {
  const [settings, setSettings] = useState<AutoModerationSettingsState>(defaultAutoModerationSettings);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const response = await api.get<{
          settings?: { autoModeration?: Partial<AutoModerationSettingsState> };
        }>('api/moderator/settings');
        if (!response.success || cancelled) return;

        const remoteSettings = response.data?.settings?.autoModeration;
        if (!remoteSettings) return;

        setSettings({
          ...defaultAutoModerationSettings,
          ...remoteSettings,
          sentimentFilter: {
            ...defaultAutoModerationSettings.sentimentFilter,
            ...remoteSettings.sentimentFilter,
          },
          duplicateDetection: {
            ...defaultAutoModerationSettings.duplicateDetection,
            ...remoteSettings.duplicateDetection,
          },
          rateLimit: {
            ...defaultAutoModerationSettings.rateLimit,
            ...remoteSettings.rateLimit,
          },
          bannedWords: Array.isArray(remoteSettings.bannedWords)
            ? remoteSettings.bannedWords
            : defaultAutoModerationSettings.bannedWords,
        });
      } catch {
        if (!cancelled) {
          toast.error('Failed to load auto-moderation settings');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await api.put('api/moderator/settings', {
        settings: {
          autoModeration: settings,
        },
      });
      if (response.success) {
        toast.success('Auto-moderation settings saved');
        onSave();
      } else {
        toast.error('Failed to save settings');
      }
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="py-6 text-sm text-muted-foreground">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="auto-mod-enabled"
          checked={settings.enabled}
          onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked as boolean }))}
        />
        <label htmlFor="auto-mod-enabled" className="text-sm font-medium">
          Enable Auto Moderation
        </label>
      </div>

      <Tabs defaultValue="content" className="space-y-4">
        <TabsList>
          <TabsTrigger value="content">Content Filters</TabsTrigger>
          <TabsTrigger value="behavior">Behavior Rules</TabsTrigger>
          <TabsTrigger value="limits">Rate Limits</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-4">
          <div>
            <label className="text-sm font-medium">Banned Words</label>
            <Textarea
              placeholder="Enter banned words, one per line"
              value={settings.bannedWords.join('\n')}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                bannedWords: e.target.value.split('\n').filter(word => word.trim())
              }))}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Maximum Comment Length</label>
            <Input
              type="number"
              value={settings.maxLength}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                maxLength: parseInt(e.target.value) || 500
              }))}
              className="mt-1"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="require-approval"
              checked={settings.requireApproval}
              onCheckedChange={(checked) => setSettings(prev => ({
                ...prev,
                requireApproval: checked as boolean
              }))}
            />
            <label htmlFor="require-approval" className="text-sm">
              Require manual approval for all comments
            </label>
          </div>
        </TabsContent>

        <TabsContent value="behavior" className="space-y-4">
          <div>
            <label className="text-sm font-medium">Spam Detection Threshold</label>
            <Input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={settings.spamThreshold}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                spamThreshold: parseFloat(e.target.value) || 0.7
              }))}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Higher values are more strict (0.0 - 1.0)
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sentiment-filter"
                checked={settings.sentimentFilter.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  sentimentFilter: { ...prev.sentimentFilter, enabled: checked as boolean }
                }))}
              />
              <label htmlFor="sentiment-filter" className="text-sm font-medium">
                Sentiment Analysis Filter
              </label>
            </div>

            {settings.sentimentFilter.enabled && (
              <div>
                <label className="text-sm">Minimum Sentiment Score</label>
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.sentimentFilter.minScore}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    sentimentFilter: {
                      ...prev.sentimentFilter,
                      minScore: parseFloat(e.target.value) || 0.3
                    }
                  }))}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="duplicate-detection"
                checked={settings.duplicateDetection.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  duplicateDetection: { ...prev.duplicateDetection, enabled: checked as boolean }
                }))}
              />
              <label htmlFor="duplicate-detection" className="text-sm font-medium">
                Duplicate Detection
              </label>
            </div>

            {settings.duplicateDetection.enabled && (
              <div>
                <label className="text-sm">Similarity Threshold</label>
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.duplicateDetection.similarityThreshold}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    duplicateDetection: {
                      ...prev.duplicateDetection,
                      similarityThreshold: parseFloat(e.target.value) || 0.8
                    }
                  }))}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="limits" className="space-y-4">
          <div>
            <label className="text-sm font-medium">Max Comments Per Minute</label>
            <Input
              type="number"
              min="1"
              value={settings.rateLimit.maxCommentsPerMinute}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                rateLimit: {
                  ...prev.rateLimit,
                  maxCommentsPerMinute: parseInt(e.target.value) || 5
                }
              }))}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Max Comments Per Hour</label>
            <Input
              type="number"
              min="1"
              value={settings.rateLimit.maxCommentsPerHour}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                rateLimit: {
                  ...prev.rateLimit,
                  maxCommentsPerHour: parseInt(e.target.value) || 20
                }
              }))}
              className="mt-1"
            />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onSave} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
